import { Geodesic } from "geographiclib-geodesic";

// Initial bearing between two GPS points on the WGS84 ellipsoid.
// Returns an angle in degrees in [0, 360[: 0 = North, 90 = East, 180 = South, 270 = West.
export const getBearing = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const { azi1 } = Geodesic.WGS84.Inverse(lat1, lon1, lat2, lon2);
  return ((azi1 ?? 0) + 360) % 360;
};

// Returns true if the vehicle is currently on a trip (last data point is more recent than all completed trip end dates).
export const isTripOngoing = (
  lastDataPointDate: string | undefined,
  completedTrips: { endDate: string }[],
): boolean => {
  if (!lastDataPointDate) return false;
  if (completedTrips.length === 0) return true;
  const lastEndDate = Math.max(
    ...completedTrips.map((t) => new Date(t.endDate).getTime()),
  );
  return new Date(lastDataPointDate).getTime() > lastEndDate;
};

// Bearing from the last two data points — used to orient the car icon when a trip is ongoing.
export const getCarBearing = (
  dataPoints: { lat: number; lon: number }[],
): number => {
  if (dataPoints.length < 2) return 0;
  const p1 = dataPoints[dataPoints.length - 2];
  const p2 = dataPoints[dataPoints.length - 1];
  return getBearing(p1.lat, p1.lon, p2.lat, p2.lon);
};

type DataPoint = { lat: number; lon: number; date: string };

// For each trip boundary (except the last), returns the data point closest in time to the trip's end date.
// Used to place the junction circle icon between consecutive trips.
export const getJunctionDataPoints = <T extends DataPoint>(
  dataPoints: T[],
  trips: { endDate: string }[],
): T[] => {
  if (dataPoints.length === 0 || trips.length <= 1) return [];
  return trips.slice(0, -1).map((trip) => {
    const endTime = new Date(trip.endDate).getTime();
    return dataPoints.reduce((closest, dp) =>
      Math.abs(new Date(dp.date).getTime() - endTime) <
      Math.abs(new Date(closest.date).getTime() - endTime)
        ? dp
        : closest,
    );
  });
};

// For each trip segment, returns the position and bearing at the midpoint.
// Used to place the directional arrow icon on each segment.
export const getArrowPositions = (
  dataPoints: DataPoint[],
  trips: { startDate: string; endDate: string }[],
): { lat: number; lon: number; bearing: number }[] => {
  if (dataPoints.length < 2 || trips.length === 0) return [];
  return trips.flatMap((trip) => {
    const startTime = new Date(trip.startDate).getTime();
    const endTime = new Date(trip.endDate).getTime();
    const segmentPoints = dataPoints
      .filter((dp) => {
        const t = new Date(dp.date).getTime();
        return t >= startTime && t <= endTime;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .filter(
        (dp, i, arr) =>
          i === 0 || dp.lat !== arr[i - 1].lat || dp.lon !== arr[i - 1].lon,
      );
    if (segmentPoints.length < 2) return [];
    const midIdx = Math.floor((segmentPoints.length - 1) / 2);
    const p1 = segmentPoints[midIdx];
    const p2 = segmentPoints[midIdx + 1];
    return [
      {
        lat: p1.lat,
        lon: p1.lon,
        bearing: getBearing(p1.lat, p1.lon, p2.lat, p2.lon),
      },
    ];
  });
};
